<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { CampaignService } from '@/services/campaign.services';
import CampaignTranslationFields from './CampaignTranslationFields.vue';
import type { ICampaign, ICampaignTranslationInput } from './campaign.types';

const props = defineProps<{
  storeId: string;
  campaignId?: string;
}>();

const emit = defineEmits<{
  saved: [campaign: ICampaign];
  cancel: [];
}>();

const isEditMode = computed(() => !!props.campaignId);
const queryClient = useQueryClient();

const { data: existingCampaign, isPending: isLoadingCampaign } = useQuery({
  queryKey: computed(() => [
    'campaign-detail-i18n',
    props.storeId,
    props.campaignId,
  ]),
  queryFn: () =>
    CampaignService.getByIdInAllLanguages(
      props.storeId,
      props.campaignId as string,
    ),
  enabled: computed(() => !!props.storeId && !!props.campaignId),
});

const lang = ref<'fr' | 'en' | 'nl'>('fr');
const translations = ref<ICampaignTranslationInput[]>([
  { languageCode: 'fr', name: '', description: '' },
  { languageCode: 'en', name: '', description: '' },
  { languageCode: 'nl', name: '', description: '' },
]);
const startAt = ref<string | null>(null);
const endAt = ref<string | null>(null);

watch(
  existingCampaign,
  (byLang) => {
    if (!byLang) return;
    translations.value = [
      {
        languageCode: 'fr',
        name: byLang.fr.name ?? '',
        description: byLang.fr.description ?? '',
      },
      {
        languageCode: 'en',
        name: byLang.en.name ?? '',
        description: byLang.en.description ?? '',
      },
      {
        languageCode: 'nl',
        name: byLang.nl.name ?? '',
        description: byLang.nl.description ?? '',
      },
    ];
    startAt.value = byLang.fr.startAt;
    endAt.value = byLang.fr.endAt;
  },
  { immediate: true },
);

function toDatetimeLocal(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const startAtLocal = computed<string>({
  get: () => toDatetimeLocal(startAt.value),
  set: (value) => {
    startAt.value = value ? new Date(value).toISOString() : null;
  },
});
const endAtLocal = computed<string>({
  get: () => toDatetimeLocal(endAt.value),
  set: (value) => {
    endAt.value = value ? new Date(value).toISOString() : null;
  },
});

const saving = ref(false);
const saveError = ref('');

async function save() {
  saveError.value = '';
  saving.value = true;
  try {
    const body = {
      translations: translations.value,
      startAt: startAt.value ?? undefined,
      endAt: endAt.value ?? undefined,
    };
    const campaign = isEditMode.value
      ? await CampaignService.update(
          props.storeId,
          props.campaignId as string,
          body,
        )
      : await CampaignService.create(props.storeId, body);
    await queryClient.invalidateQueries({ queryKey: ['campaigns-list'] });
    emit('saved', campaign);
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    saveError.value = Array.isArray(raw)
      ? raw.join(' · ')
      : (raw ?? err?.message ?? 'Erreur lors de l’enregistrement');
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="cfp">
    <div class="cfp__header">
      <h2 class="cfp__title">
        {{ isEditMode ? 'Modifier la campagne' : 'Nouvelle campagne' }}
      </h2>
      <button class="cfp__btn cfp__btn--ghost" @click="emit('cancel')">
        Annuler
      </button>
    </div>

    <div v-if="isEditMode && isLoadingCampaign" class="cfp__loading">
      Chargement…
    </div>

    <template v-else>
      <section class="cfp__section">
        <h3 class="cfp__section-title">Nom &amp; description</h3>
        <CampaignTranslationFields
          v-model:lang="lang"
          v-model:translations="translations"
        />
      </section>

      <section class="cfp__section">
        <h3 class="cfp__section-title">Période</h3>
        <div class="cfp__field-row">
          <div class="cfp__field">
            <label class="cfp__label" for="cfp-start-at">Début</label>
            <input
              id="cfp-start-at"
              v-model="startAtLocal"
              class="cfp__input"
              type="datetime-local"
            />
          </div>
          <div class="cfp__field">
            <label class="cfp__label" for="cfp-end-at">Fin</label>
            <input
              id="cfp-end-at"
              v-model="endAtLocal"
              class="cfp__input"
              type="datetime-local"
            />
          </div>
        </div>
      </section>

      <p v-if="saveError" class="cfp__error">{{ saveError }}</p>

      <div class="cfp__actions">
        <button
          class="cfp__btn cfp__btn--primary"
          :disabled="saving"
          @click="save"
        >
          <i v-if="saving" class="pi pi-spin pi-spinner" />
          Enregistrer
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.cfp {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cfp__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.cfp__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cfp__loading {
  padding: 3rem 1rem;
  text-align: center;
  color: var(--nxt-on-surface-muted);
}
.cfp__section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  background: var(--nxt-card-bg);
}
.cfp__section-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cfp__field-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.cfp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
  min-width: 12rem;
}
.cfp__label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.cfp__input,
.cfp__textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}
.cfp__input:focus,
.cfp__textarea:focus {
  outline: none;
  border-color: var(--nxt-primary);
  box-shadow: 0 0 0 3px rgba(var(--nxt-primary-rgb, 246, 132, 2), 0.15);
}
.cfp__textarea {
  resize: vertical;
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 0.8125rem;
}
.cfp__field-error {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-error);
}
.cfp__error {
  margin: 0;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.1);
  color: var(--nxt-error);
  font-size: 0.8125rem;
}
.cfp__actions {
  display: flex;
  justify-content: flex-end;
}
.cfp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
}
.cfp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.cfp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
.cfp__btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
